import { Phone, Mail, MessageCircle, Linkedin } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { Reveal, Chapter } from "@/components/site/Reveal";
import { PARTNERS } from "@/data/content";
import { gmailTo, ENQUIRY_SUBJECT } from "@/lib/email";

const actionCls =
  "flex items-center gap-2.5 border border-slateink/25 px-5 py-3 font-mono text-[11px] tracking-[0.14em] text-slateink transition-all duration-300 hover:border-gold hover:bg-ink hover:text-gold";

const ContactActions = ({ p, idx, center = true }) => (
  <div className={`mt-6 flex flex-wrap gap-2.5 ${center ? "justify-center" : ""}`}>
    {p.phoneHref && (
      <a data-testid={`partner-${idx}-call`} href={p.phoneHref} className={actionCls}>
        <Phone size={13} strokeWidth={1.5} /> {p.phone}
      </a>
    )}
    {p.email && (
      <a data-testid={`partner-${idx}-email`} href={gmailTo(p.email, ENQUIRY_SUBJECT)} target="_blank" rel="noopener noreferrer" className={`${actionCls} max-w-full`}>
        <Mail size={13} strokeWidth={1.5} className="shrink-0" /> <span className="break-all">{p.email}</span>
      </a>
    )}
    {p.whatsapp && (
      <a data-testid={`partner-${idx}-whatsapp`} href={p.whatsapp} target="_blank" rel="noopener noreferrer" className={actionCls}>
        <MessageCircle size={13} strokeWidth={1.5} /> WhatsApp
      </a>
    )}
    {p.linkedin && (
      <a data-testid={`partner-${idx}-linkedin`} href={p.linkedin} target="_blank" rel="noopener noreferrer" className={actionCls}>
        <Linkedin size={13} strokeWidth={1.5} /> LinkedIn
      </a>
    )}
  </div>
);

export default function About() {
  const vidRef = useRef(null);
  useEffect(() => {
    vidRef.current?.play().catch(() => {});
  }, []);
  return (
    <section id="about" data-testid="leadership-section" className="bg-ivory text-slateink">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <Chapter n="Chapter 09" label="About VERIVISTA" />
        <div className="mb-20 flex flex-col items-center py-6 text-center">
          <div className="w-screen shrink-0">
            <motion.video
              ref={vidRef}
              aria-label="VERIVISTA Advisory LLP — animated signature"
              data-testid="about-brand-logo"
              className="aspect-video w-full mix-blend-multiply"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              initial={{ scale: 1.15, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ type: "spring", stiffness: 220, damping: 16, mass: 1 }}
            >
              <source src="/assets/verivista-signature-hd.webm" type="video/webm" />
              <source src="/assets/verivista-signature-hd.mp4" type="video/mp4" />
            </motion.video>
          </div>
          <motion.span
            className="gold-hairline mt-10 w-24"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.55, duration: 0.6 }}
          />
          <motion.p
            className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-bodylight"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            Transparency in Numbers. Trust in Every Frame.
          </motion.p>
        </div>
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="text-balance font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                An advisory house for the economics of storytelling.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-bodylight sm:text-lg">
                VERIVISTA Advisory LLP was founded on a single conviction: screen and media
                productions deserve the same financial rigour as any institutional enterprise —
                delivered by specialists who understand sets, schedules and studios, not just
                spreadsheets.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-base leading-relaxed text-bodylight sm:text-lg">
                We work alongside producers, production houses, OTT teams and broadcasters as an
                integrated partner — structuring deals, auditing every transaction and keeping the
                project ledger immaculate from greenlight to final rebate. We are fluent in the
                realities of Indian production finance: GST-compliant invoicing, TDS deductions,
                state shooting incentives and multi-location statutory compliance.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 border-l-2 border-gold pl-6">
                <p className="font-serif text-xl italic leading-snug text-slateink sm:text-2xl">
                  Precision is not a department. It is the culture of the production.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-bodylight">The Leadership</p>
            </Reveal>
            <div className="mt-8 space-y-6">
              {PARTNERS.map((p, i) => (
                <Reveal key={p.id} delay={0.15 + i * 0.12}>
                  <div
                    data-testid={i === 0 ? "leader-ceo" : "leader-exec-commercial"}
                    className="group border border-slateink/15 bg-white/50 p-8 transition-all duration-500 hover:border-gold/50"
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-serif text-2xl font-medium">{p.name}</h3>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-gold">0{i + 1}</span>
                    </div>
                    <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bodylight">{p.role}</p>
                    <ContactActions p={p} idx={i + 1} center={false} />
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.4}>
              <p className="mt-8 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-bodylight/80">
                Partner-led engagements. No account managers, no hand-offs.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
